if (!customElements.get('vl-pdp-feature-card')) {
  class VLPDPFeatureCard extends HTMLElement {
    constructor() {
      super();

      this.abortController = null;
      this.videoObserver = null;
      this.externalVideo = null;
      this.playButton = null;
      this.iframe = null;
      this.isInView = false;
      this.playbackRetryTimer = null;
      this.handlePlayClick = this.handlePlayClick.bind(this);
      this.handleIntersection = this.handleIntersection.bind(this);
      this.handleIframeLoad = this.handleIframeLoad.bind(this);
    }

    connectedCallback() {
      this.externalVideo = this.querySelector('[data-vl-feature-external-video]');
      if (!this.externalVideo) return;

      this.abortController = new AbortController();
      this.playButton = this.externalVideo.querySelector('[data-vl-feature-video-play]');
      this.playButton?.addEventListener('click', this.handlePlayClick, {
        signal: this.abortController.signal,
      });

      this.initObserver();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.videoObserver?.disconnect();
      window.clearTimeout(this.playbackRetryTimer);
      this.abortController = null;
      this.videoObserver = null;
      this.iframe = null;
      this.isInView = false;
      this.playbackRetryTimer = null;
    }

    initObserver() {
      if (!('IntersectionObserver' in window)) {
        this.isInView = true;
        if (this.isAutoplay) this.loadVideo(true);
        return;
      }

      this.videoObserver = new IntersectionObserver(this.handleIntersection, {
        rootMargin: '0px',
        threshold: 0.2,
      });
      this.videoObserver.observe(this);
    }

    handleIntersection(entries) {
      entries.forEach((entry) => {
        this.isInView = entry.isIntersecting;

        if (entry.isIntersecting) {
          if (this.isAutoplay) {
            this.loadVideo(true);
            this.postCommand('playVideo');
          }
          return;
        }

        this.postCommand('pauseVideo');
      });
    }

    handlePlayClick() {
      this.loadVideo(true);
    }

    loadVideo(autoplay) {
      if (this.iframe || !this.externalVideo) return;

      const videoId = this.externalVideo.dataset.videoId;
      if (!videoId) return;

      const iframe = document.createElement('iframe');
      const loop = this.isAutoplay ? `&loop=1&playlist=${encodeURIComponent(videoId)}` : '';
      const controls = this.isAutoplay ? 0 : 1;
      const origin = encodeURIComponent(window.location.origin);

      iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(
        videoId
      )}?autoplay=${autoplay ? 1 : 0}&mute=${this.isAutoplay ? 1 : 0}&controls=${controls}&playsinline=1&rel=0&enablejsapi=1&origin=${origin}${loop}`;
      iframe.title = 'Feature video';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      iframe.setAttribute('allowfullscreen', '');
      iframe.addEventListener('load', this.handleIframeLoad, { once: true });

      this.externalVideo.appendChild(iframe);
      this.externalVideo.classList.add('is-playing');
      this.iframe = iframe;
    }

    handleIframeLoad() {
      if (!this.isAutoplay || !this.isInView) return;

      this.postCommand('mute');
      this.postCommand('playVideo');

      window.clearTimeout(this.playbackRetryTimer);
      this.playbackRetryTimer = window.setTimeout(() => {
        if (!this.isInView) return;
        this.postCommand('mute');
        this.postCommand('playVideo');
      }, 500);
    }

    postCommand(command) {
      if (!this.iframe?.contentWindow) return;

      this.iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: command, args: [] }),
        'https://www.youtube-nocookie.com'
      );
    }

    get isAutoplay() {
      return this.externalVideo?.dataset.videoMode === 'autoplay_loop';
    }

  }

  customElements.define('vl-pdp-feature-card', VLPDPFeatureCard);
}
